import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { Heart, User, Mail, Lock, ArrowLeft, CheckCircle, Phone, Shield, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const returnUrl = searchParams.get('return') || searchParams.get('redirect') || '/';
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [accountLocked, setAccountLocked] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);

  // Basic validation functions
  const validatePhoneNumber = (phone) => {
    if (!phone || phone.trim() === '') {
      return { isValid: true, error: null }; // Phone is optional
    }
    
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length < 7 || cleaned.length > 15) {
      return { isValid: false, error: 'Please enter a valid phone number' };
    }
    
    return { isValid: true, error: null };
  };

  const validateForm = () => {
    const errors = {};

    // Basic email validation
    if (!formData.email.includes('@') || !formData.email.includes('.')) {
      errors.email = 'Please enter a valid email address';
    }

    // Phone validation (if provided)
    const phoneValidation = validatePhoneNumber(formData.phone);
    if (!phoneValidation.isValid) {
      errors.phone = phoneValidation.error;
    }

    // Basic password validation for registration
    if (!isLogin) {
      if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters long';
      }

      if (!formData.fullName.trim()) {
        errors.fullName = 'Full name is required';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (accountLocked) {
      setError('Account temporarily locked due to multiple failed attempts. Please try again later.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      let result;
      if (isLogin) {
        // Login
        result = await login(formData.email, formData.password);
        if (result.success) {
          setFailedAttempts(0);
          navigate(returnUrl);
        } else {
          const newFailedAttempts = failedAttempts + 1;
          setFailedAttempts(newFailedAttempts);
          
          if (newFailedAttempts >= 5) {
            setAccountLocked(true);
            setError('Account locked due to too many failed attempts. Please try again in 15 minutes.');
            // Auto-unlock after 15 minutes
            setTimeout(() => {
              setAccountLocked(false);
              setFailedAttempts(0);
            }, 15 * 60 * 1000);
          } else {
            setError(`${result.error} (${5 - newFailedAttempts} attempts remaining)`);
          }
        }
      } else {
        // Handle registration with verification
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
          email: formData.email,
          password: formData.password,
          full_name: formData.fullName,
          phone: formData.phone
        });

        if (response.data.verification_required) {
          setShowVerificationMessage(true);
          setFormData({ email: '', password: '', fullName: '' });
        }
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Please verify your email address before logging in. Check your inbox for the verification link.');
        setShowResendVerification(true);
      } else {
        setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const resendVerificationEmail = async () => {
    if (!formData.email.trim()) {
      alert('Please enter your email address');
      return;
    }

    setResendingVerification(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification`, {
        email: formData.email
      });
      alert('Verification email sent! Please check your inbox.');
      setShowResendVerification(false);
      setError('');
    } catch (error) {
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setResendingVerification(false);
    }
  };

  return (
    <div className="min-h-screen hero-bg flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-600 hover:text-rose-600 mb-8 transition-colors"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </button>

        {/* Auth Card */}
        <div className="glass-card p-8 fade-in" data-testid="auth-form">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Heart className="w-8 h-8 text-rose-600" />
              <span className="text-2xl font-bold gradient-text">LuvCup</span>
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">
              {showVerificationMessage ? 'Check Your Email!' : (isLogin ? 'Welcome Back' : 'Join LuvCup')}
            </h2>
            <p className="text-gray-600">
              {showVerificationMessage 
                ? 'We\'ve sent you a verification link' 
                : (isLogin 
                    ? 'Continue your love journey' 
                    : 'Start exploring your relationships'
                  )
              }
            </p>
          </div>

          {showVerificationMessage && (
            <div className="text-center mb-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Registration Successful!</h3>
              <p className="text-gray-600 mb-4">
                Please check your email inbox for a verification link to activate your account.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Next Steps:</h4>
                <ul className="text-sm text-blue-700 text-left space-y-1">
                  <li>1. Check your email inbox (and spam folder)</li>
                  <li>2. Click the verification link</li>
                  <li>3. Return here to log in and start your tests!</li>
                </ul>
              </div>
              <button
                onClick={() => {
                  setShowVerificationMessage(false);
                  setIsLogin(true);
                }}
                className="btn-primary"
                data-testid="proceed-to-login-btn"
              >
                Proceed to Login
              </button>
            </div>
          )}

          {!showVerificationMessage && (
            <>
            {error && (
              <div className={`border rounded-lg p-3 mb-4 ${
                accountLocked ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center">
                  {accountLocked ? (
                    <Shield className="w-5 h-5 text-orange-600 mr-2" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                  )}
                  <p className={`text-sm ${accountLocked ? 'text-orange-700' : 'text-red-700'}`}>
                    {error}
                  </p>
                </div>
              </div>
            )}

            {/* Security Notice for Registration */}
            {!isLogin && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-start">
                  <Shield className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-800 mb-1">Security Features</h4>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Strong password requirements for account protection</li>
                      <li>• Disposable email addresses are blocked</li>
                      <li>• Account verification required before access</li>
                      <li>• Rate limiting prevents unauthorized access attempts</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
                        validationErrors.fullName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="Enter your full name"
                      required={!isLogin}
                      data-testid="fullname-input"
                    />
                  </div>
                  {validationErrors.fullName && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.fullName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number (Optional)
                  </label>
                  <div className="relative">
                    <Phone className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
                        validationErrors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="+1 (555) 123-4567"
                      data-testid="phone-input"
                    />
                  </div>
                  {validationErrors.phone && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
                  )}
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
                    validationErrors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Enter your email"
                  required
                  data-testid="email-input"
                />
              </div>
              {validationErrors.email && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
                    validationErrors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Enter your password"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              
              {/* Basic password guidance for registration */}
              {!isLogin && formData.password && (
                <div className="mt-2 text-xs text-gray-600">
                  Password should be at least 6 characters long
                </div>
              )}
              
              {validationErrors.password && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.password}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center"
              data-testid="submit-btn"
            >
              {loading ? (
                <div className="heart-spinner"></div>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          {showResendVerification && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 mb-3">
                Need a new verification email?
              </p>
              <button
                onClick={resendVerificationEmail}
                disabled={resendingVerification}
                className="w-full btn-secondary flex items-center justify-center text-sm"
                data-testid="resend-verification-btn"
              >
                {resendingVerification ? (
                  <>
                    <div className="spinner w-4 h-4 mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Verification Email
                  </>
                )}
              </button>
            </div>
          )}

          <div className="mt-6 text-center space-y-3">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setShowResendVerification(false);
                setFormData({ email: '', password: '', fullName: '' });
              }}
              className="text-rose-600 hover:text-rose-700 font-medium block"
              data-testid="toggle-auth-mode-btn"
            >
              {isLogin 
                ? "Don't have an account? Sign up" 
                : "Already have an account? Sign in"
              }
            </button>

            {isLogin && (
              <button
                onClick={() => navigate('/forgot-password')}
                className="text-gray-600 hover:text-rose-600 font-medium text-sm"
                data-testid="forgot-password-btn"
              >
                Forgot your password?
              </button>
            )}
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;